import { Link } from "react-router-dom";
import { Duplicates } from "@/components/customer/Duplicates";
import { initialsOf } from "@/components/patterns";
import { formatINRCompact } from "@/lib/utils";
import type { ContactRecord, DealRecord } from "@/types";
import type { Task } from "@/types/task";
import { dateLocale, withClock } from "@/lib/dateTime";

const Card = ({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) => (
  <section className="flex flex-col gap-1 rounded-lg border border-border bg-card px-3.5 py-3">
    <div className="mb-1 flex items-center justify-between">
      <h2 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      {aside}
    </div>
    {children}
  </section>
);
const full = (c: ContactRecord) => `${c.first_name} ${c.last_name || ""}`.trim();
const when = (d: string) => new Date(d).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }));

/** People, open deals, possible duplicates and what is due next, beside the account's activity */
export function AccountRelated({
  accountId,
  contacts,
  deals,
  tasks,
  can,
  onAddContact,
  onTask,
  onDoneTask,
  onMerged,
}: {
  accountId: string;
  contacts: ContactRecord[];
  deals: DealRecord[];
  tasks: Task[];
  can: { contact: boolean; task: boolean; merge: boolean };
  onAddContact: () => void;
  onTask: () => void;
  onDoneTask: (t: Task) => void;
  onMerged: () => void;
}) {
  const open = deals.filter((d) => d.deal_status === "OPEN");
  const openValue = open.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
  const due = tasks.filter((t) => t.task_status === "OPEN").sort((a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime());

  return (
    <div className="flex flex-col gap-3">
      <Card
        title={`People · ${contacts.length}`}
        aside={
          can.contact && (
            <button type="button" onClick={onAddContact} className="text-xs text-primary hover:underline">
              + Add
            </button>
          )
        }
      >
        {contacts.length === 0 && <span className="text-xs text-muted-foreground">No one added yet.</span>}
        {contacts.slice(0, 6).map((c) => (
          <Link key={c.id} to={`/contacts/${c.id}`} className="flex items-center gap-2.5 border-t border-border/60 py-1.5 first:border-t-0">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-accent-foreground">{initialsOf(full(c))}</span>
            <span className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="truncate text-[13px] font-semibold text-foreground">{full(c)}</span>
              <span className="truncate text-xs text-muted-foreground">{c.job_title || "—"}</span>
            </span>
            {c.is_primary && <span className="rounded bg-accent px-1.5 text-[10.5px] font-semibold leading-[18px] text-accent-foreground">Primary</span>}
          </Link>
        ))}
        {contacts.length > 6 && <span className="pt-1 text-xs text-muted-foreground">and {contacts.length - 6} more in the Contacts tab</span>}
      </Card>

      <Card title={`Open deals · ${open.length}`} aside={open.length > 0 && <span className="font-mono text-xs font-semibold">{formatINRCompact(openValue)}</span>}>
        {open.length === 0 && <span className="text-xs text-muted-foreground">No open deals.</span>}
        {open.slice(0, 5).map((d) => {
          const p = Math.max(0, Math.min(100, Number(d.probability) || 0));
          return (
            <div key={d.id} className="flex flex-col gap-1 border-t border-border/60 py-1.5 first:border-t-0">
              <div className="flex justify-between gap-2">
                <Link to={`/deals/${d.id}`} className="truncate text-[13px] font-semibold text-primary hover:underline">
                  {d.name}
                </Link>
                <span className="font-mono text-xs">{formatINRCompact(d.amount)}</span>
              </div>
              <div className="flex justify-between gap-2 text-[11.5px] text-muted-foreground">
                <span>{d.stage?.name || "—"}</span>
                <span>{d.expected_close_date ? `Closes ${new Date(d.expected_close_date).toLocaleDateString(dateLocale(), { day: "numeric", month: "short" })}` : "No close date"}</span>
              </div>
              <div className="h-1 rounded-sm bg-border">
                <div className="h-full rounded-sm bg-primary" style={{ width: `${p}%` }} />
              </div>
            </div>
          );
        })}
      </Card>

      <Duplicates type="ACCOUNT" id={accountId} canMerge={can.merge} onMerged={onMerged} />

      <Card title="Next follow-up">
        {due.length === 0 && <span className="text-xs text-muted-foreground">Nothing scheduled.</span>}
        {due.slice(0, 3).map((t) => (
          <label key={t.id} className="flex items-center gap-2.5 rounded-lg border border-border px-2.5 py-2">
            <input type="checkbox" className="size-4 accent-primary" onChange={() => onDoneTask(t)} aria-label={`Mark "${t.title}" done`} />
            <span className="min-w-0 flex-1 truncate text-[13px]">{t.title}</span>
            <span className="font-mono text-xs text-primary">{when(t.due_at)}</span>
          </label>
        ))}
        {can.task && (
          <button type="button" onClick={onTask} className="pt-1 text-left text-[13px] font-medium text-primary hover:underline">
            + Add task
          </button>
        )}
      </Card>
    </div>
  );
}
